import React, { useState, useMemo } from 'react';
import { format, isAfter, isBefore, isEqual, startOfDay } from 'date-fns';
import { Calendar as CalendarIcon, TrendingUp, PieChart, Layers, Landmark, Package, FlaskConical } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useOperationalExpenses } from '@/hooks/useOperationalExpenses';
import { useManualReceivables } from '@/hooks/useManualReceivables';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const fmt = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const parseInvoiceDate = (dStr) => {
  if (!dStr) return 0;
  let d = new Date(dStr);
  if (isNaN(d.getTime()) && typeof dStr === 'string') {
    const str = dStr.toLowerCase();
    const months = { 'jan':0,'feb':1,'mar':2,'apr':3,'mei':4,'may':4,'jun':5,'jul':6,'agu':7,'aug':7,'sep':8,'okt':9,'oct':9,'nov':10,'des':11,'dec':11 };
    const parts = str.split(' ');
    if (parts.length >= 3) {
       const day = parseInt(parts[0]);
       const monthStr = Object.keys(months).find(m => parts[1].includes(m));
       const month = monthStr ? months[monthStr] : 0;
       const year = parseInt(parts[2]);
       d = new Date(year, month, day);
    }
  }
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

function isInRange(dateStr, start, end) {
  if (!dateStr) return false;
  try {
    const timestamp = parseInvoiceDate(dateStr);
    if (timestamp === 0) return false;
    const d = startOfDay(new Date(timestamp));
    const s = startOfDay(new Date(start));
    const e = startOfDay(new Date(end));
    return (isAfter(d, s) || isEqual(d, s)) && (isBefore(d, e) || isEqual(d, e));
  } catch { return false; }
}

const presets = [
  { label: 'Hari ini', getValue: () => [format(new Date(), 'yyyy-MM-dd'), format(new Date(), 'yyyy-MM-dd')] },
  { label: '7 Hari', getValue: () => { const now = new Date(); return [format(new Date(now - 6 * 86400000), 'yyyy-MM-dd'), format(now, 'yyyy-MM-dd')]; } },
  { label: '30 Hari', getValue: () => { const now = new Date(); return [format(new Date(now - 29 * 86400000), 'yyyy-MM-dd'), format(now, 'yyyy-MM-dd')]; } },
  { label: 'Bulan Ini', getValue: () => { const now = new Date(); return [format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'), format(now, 'yyyy-MM-dd')]; } },
  { label: 'Semua', getValue: () => ['2000-01-01', '2099-12-31'] },
];

export default function LaporanKeuangan() {
  const [activePreset, setActivePreset] = useState('Semua');
  const [dateStart, setDateStart] = useState('2000-01-01');
  const [dateEnd, setDateEnd] = useState('2099-12-31');
  const [isCustom, setIsCustom] = useState(false);

  const { clientPOs = [] } = useClientPOs();
  const { expenses = [] } = useOperationalExpenses();
  const { data: accountMutations = [] } = useQuery({
    queryKey: ['account-mutations'],
    queryFn: () => base44.entities.AccountMutation.list('-created_date'),
    staleTime: 5 * 60 * 1000,
  });

  const mergedExpenses = useMemo(() => {
    const payrolls = accountMutations
      .filter(m => m.reference_id?.startsWith('bulk-payroll-') && m.type === 'keluar')
      .map(m => ({
        id: `payroll-${m.id}`,
        tanggal: m.date || m.created_date?.split('T')[0] || '',
        nominal: m.amount,
      }));
    return [...expenses, ...payrolls];
  }, [expenses, accountMutations]);

  const { manualReceivables = [] } = useManualReceivables();
  const { data: requests = [] } = useQuery({
    queryKey: ['purchasing-requests'],
    queryFn: () => base44.entities.PurchasingRequest.list('-created_date'),
  });

  // Fetch Bank Accounts and Mutations to get Total Kas
  const { accountsMap: accounts } = useBankAccounts();
  const { data: dbMutations = [] } = useQuery({
    queryKey: ['account-mutations'],
    queryFn: () => base44.entities.AccountMutation.list()
  });

  const getAccountKeyOfMutation = (mutationAccountName) => {
    const name = (mutationAccountName || '').toLowerCase().trim();
    if (accounts?.bca?.name && name === accounts.bca.name.toLowerCase().trim()) return 'bca';
    if (accounts?.mandiri?.name && name === accounts.mandiri.name.toLowerCase().trim()) return 'mandiri';
    if (accounts?.bni?.name && name === accounts.bni.name.toLowerCase().trim()) return 'bni';
    if (name.includes('bca')) return 'bca';
    if (name.includes('mandiri') || name.includes('qris')) return 'mandiri';
    if (name.includes('bni')) return 'bni';
    return 'bca';
  };

  const totalKasBank = useMemo(() => {
    if (!accounts || !accounts.bca) return 0;
    const balances = {
      bca: Number(accounts.bca.startBalance) || 0,
      mandiri: Number(accounts.mandiri.startBalance) || 0,
      bni: Number(accounts.bni.startBalance) || 0
    };
    dbMutations.forEach(m => {
      const key = getAccountKeyOfMutation(m.account);
      const amt = Number(m.amount) || 0;
      if (m.type === 'masuk') balances[key] += amt;
      else balances[key] -= amt;
    });
    return Object.values(balances).reduce((sum, b) => sum + b, 0);
  }, [dbMutations, accounts]);

  const handlePreset = (preset) => {
    const [s, e] = preset.getValue();
    setDateStart(s);
    setDateEnd(e);
    setActivePreset(preset.label);
    setIsCustom(false);
  };

  
  const metrics = useMemo(() => {
    const res = {
      standar: { omset: 0, hpp: 0, piutang: 0, ppn: 0 },
      custom: { omset: 0, hpp: 0, piutang: 0, ppn: 0 },
      sample: { omset: 0, hpp: 0, piutang: 0, ppn: 0 },
      biayaOperasional: 0,
      hutang: 0
    };

    clientPOs.forEach(po => {
      const isSelesai = po.statusProduksi === 'PESANAN SELESAI' || po.statusProduksi === 'SELESAI';
      const d = (isSelesai && (po.updated_at || po.rawRecord?.updated_at)) ? (po.updated_at || po.rawRecord.updated_at).split('T')[0] : (po.date || po.created_at?.split('T')[0] || po.tanggal_invoice || po.rawRecord?.created_at || '');
      if (!isInRange(d, dateStart, dateEnd)) return;

      const isCustom = po.poMode === 'custom_finance' || po.is_custom_finance === true;
      const isSample = po.poMode === 'sample' || po.isSample === true;

      // === PERHITUNGAN HPP & OMSET ===
      let poProdJual = 0;
      let poProdHpp = 0;
      let legJual = 0;
      let legHpp = 0;

      // 1. Hitung Produk
      if (isCustom) {
          // Logic FinanceCustomInvoices.jsx
          const rawProducts = po.products || po.items || [];
          rawProducts.forEach(p => {
             poProdJual += (p.total_harga_jual || p.subtotal || p.hargaTotal || 0);
             const qty = p.qty || 1;
             poProdHpp += ((p.hargaBeli || p.harga_beli || 0) * qty);
          });
      } else {
          // Logic FinanceInvoices.jsx (Standar) & ProfitSample.jsx (Sample)
          // Murni hanya membaca po.products (sesuai invoice)
          const products = po.products || [];
          products.forEach(p => {
             poProdJual += (p.total_harga_jual || p.subtotal || 0);
             
             if (p.total_hpp_product !== undefined && Number(p.total_hpp_product) > 0) {
               poProdHpp += Number(p.total_hpp_product);
             } else {
               const qty = p.qty || 1;
               const modalPackaging = (p.botol_harga_beli || 0) + (p.tutup_harga_beli || 0) + (p.spray_harga_beli || 0) + (p.fullset_harga_beli || 0);
               const volume = p.botol_ukuran_client || p.botol_ukuran_label || p.ukuran_botol_ml || p.volume_ml || 0;
               let racikan = p.racikan || [];
               if (typeof racikan === 'string') try { racikan = JSON.parse(racikan); } catch(e) { racikan = []; }
               const modalRacikan = racikan.reduce((rs, r) => {
                 const costPerUnit = r.hpp_bahan || r.harga_per_satuan || 0;
                 const volPerPcs = volume * ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100);
                 return rs + volPerPcs * (costPerUnit / 1000);
               }, 0);
               let komponenTambahan = p.komponen_tambahan || [];
               if (typeof komponenTambahan === 'string') try { komponenTambahan = JSON.parse(komponenTambahan); } catch(e) { komponenTambahan = []; }
               const modalKomponen = komponenTambahan.reduce((ks, k) => ks + (k.harga || 0), 0);
               const customHargaBeli = p.hargaBeli || p.harga_beli || 0;
               // JASA TIDAK DIHITUNG SEBAGAI HPP (SESUAI FINANCE INVOICE)
               const hppPerPcs = modalPackaging + modalRacikan + modalKomponen + customHargaBeli;
               poProdHpp += (hppPerPcs * qty);
             }
             
             // Karena total_hpp_product di atas (jika ada) mungkin sudah memasukkan jasa,
             // kita perlu mengurangi totalJasa dari poProdHpp persis seperti di FinanceInvoices.jsx
             // line 860: const totalJasa = ... return s + (inv.totalHpp - totalJasa)
             const jasaPcs = p.jasa_nominal || p.final_jasa_nominal || 0;
             const qtyPcs = p.qty || 1;
             
             if (p.total_hpp_product !== undefined && Number(p.total_hpp_product) > 0) {
               poProdHpp -= (jasaPcs * qtyPcs); // Kurangi jasa jika menggunakan total_hpp_product (karena FinanceInvoices juga menguranginya dari totalHpp)
             }
          });
      }

      // 2. Hitung Legalitas
      let rawLegalitas = po.legalitas || [];
      if (typeof rawLegalitas === 'string') try { rawLegalitas = JSON.parse(rawLegalitas); } catch(e) { rawLegalitas = []; }
      rawLegalitas.forEach(l => {
        const qty = l.qty_variant || 1;
        legJual += (l.harga_jual || 0) * qty;
        legHpp += (l.harga_beli || 0) * qty;
      });

      // Omset MURNI dari Harga Jual Produk + Legalitas (Sesuai Invoice, tanpa Ongkir & Jasa Desain)
      const totalJualSemua = poProdJual + legJual;
      const nominalPPN = po.ppn_mode === 'exclude' ? (totalJualSemua * (parseFloat(po.ppn_percentage) || 0) / 100) : 0;
      const totalOmset = totalJualSemua; // Omset tanpa PPN
      const totalTagihan = totalOmset + nominalPPN;
      const totalHppSemua = poProdHpp + legHpp;

      let dibayar = (po.riwayatPembayaran || []).reduce((s, r) => s + (r.jumlah || 0), 0);
      const status = String(po.statusBayar || 'Belum Bayar');
      if (status === 'Sudah Lunas' || status === 'LUNAS' || status.toUpperCase() === 'LUNAS') {
        dibayar = totalTagihan;
      } else if (status === 'DP 50%') {
        dibayar = Math.max(dibayar, totalTagihan / 2);
      }

      const sisaTagihan = totalTagihan - dibayar;
      const actualPiutang = sisaTagihan > 0 ? sisaTagihan : 0;

      const isTempo = po.is_tempo || po.statusBayar === 'TEMPO' || po.paymentTerms === 'tempo' || po.rawRecord?.is_tempo || po.rawRecord?.paymentTerms === 'tempo';
      const hasPayment = dibayar > 0;

      if (isCustom) {
         res.custom.omset += totalOmset;
         res.custom.hpp += totalHppSemua;
         res.custom.ppn += nominalPPN;
         if (isTempo || hasPayment) res.custom.piutang += actualPiutang;
      } else if (isSample) {
         res.sample.omset += totalOmset;
         res.sample.hpp += totalHppSemua;
         res.sample.ppn += nominalPPN;
         if (isTempo || hasPayment) res.sample.piutang += actualPiutang;
      } else {
         res.standar.omset += totalOmset;
         res.standar.hpp += totalHppSemua;
         res.standar.ppn += nominalPPN;
         if (isTempo || hasPayment) res.standar.piutang += actualPiutang;
      }
    });

    // Tambahkan Piutang Manual (masuk ke Standar)
    manualReceivables.forEach(mr => {
      const d = mr.created_date || mr.tanggal;
      if (!isInRange(d, dateStart, dateEnd)) return;
      if (mr.status !== 'Lunas') {
        res.standar.piutang += (mr.total || 0);
      }
    });

    res.biayaOperasional = mergedExpenses
      .filter(ex => isInRange(ex.date || ex.created_date || ex.tanggal, dateStart, dateEnd))
      .reduce((s, ex) => s + (Number(ex.amount || ex.nominal) || 0), 0);

    res.hutang = requests
      .filter(req => isInRange(req.created_date, dateStart, dateEnd))
      .filter(req => req.metode_pembayaran === 'Tempo' && req.status_pembayaran_tempo === 'Menunggu Jatuh Tempo')
      .reduce((s, req) => s + (req.total_biaya || 0), 0);

    return res;
  }, [clientPOs, mergedExpenses, requests, manualReceivables, dateStart, dateEnd]);


  return (
    <div className="space-y-4 md:space-y-6 max-w-7xl mx-auto pb-24 md:pb-6 relative animate-in fade-in zoom-in-95 duration-500 font-inter">
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-black text-[#302621] tracking-tight">Laporan Keuangan</h1>
          <p className="text-xs md:text-sm text-[#7A695B] mt-1 font-medium">Gambaran Arus Kas, Margin, Piutang & Hutang Pabrik</p>
        </div>
      </div>

      {/* FILTER SECTION */}
      <div className="bg-white border border-[#E6D5B8] p-3 md:p-4 rounded-xl md:rounded-2xl shadow-sm flex flex-col xl:flex-row xl:items-center gap-4">
        <div className="flex items-center gap-2 text-xs md:text-sm font-bold text-[#8C6208] min-w-max uppercase tracking-wider">
          <CalendarIcon className="w-4 h-4 md:w-5 md:h-5" /> PERIODE:
        </div>
        <div className="flex flex-wrap gap-2 flex-1">
          {presets.map(p => (
            <button
              key={p.label}
              onClick={() => handlePreset(p)}
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-[10px] md:text-xs font-bold transition-all whitespace-nowrap shadow-sm border ${
                activePreset === p.label && !isCustom
                  ? 'bg-[#E3E8EE] text-[#4B5563] border-[#D1D5DB]'
                  : 'bg-[#F3F4F6] text-[#6B7280] border-transparent hover:bg-[#E5E7EB]'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => { setIsCustom(true); setActivePreset('Custom'); }}
            className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-[10px] md:text-xs font-bold transition-all shadow-sm border ${
              isCustom
                ? 'bg-[#302621] text-white border-[#302621]'
                : 'bg-[#F3F4F6] text-[#6B7280] border-transparent hover:bg-[#E5E7EB]'
            }`}
          >
            Custom
          </button>
        </div>
        
        <div className="flex items-center gap-2 xl:ml-auto w-full xl:w-auto overflow-x-auto pb-1 xl:pb-0">
          <input
            type="date"
            value={dateStart}
            onChange={(val) => { setDateStart(val); setIsCustom(true); setActivePreset('Custom'); }}
            className="border border-[#E6D5B8] rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-[10px] md:text-xs font-medium text-[#504035] bg-[#F9F5EC] focus:ring-2 focus:ring-[#D4AF37] outline-none min-w-[120px]"
          />
          <span className="text-[#A39171] font-medium text-xs">s/d</span>
          <input
            type="date"
            value={dateEnd}
            onChange={(val) => { setDateEnd(val); setIsCustom(true); setActivePreset('Custom'); }}
            className="border border-[#E6D5B8] rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-[10px] md:text-xs font-medium text-[#504035] bg-[#F9F5EC] focus:ring-2 focus:ring-[#D4AF37] outline-none min-w-[120px]"
          />
        </div>
      </div>

      {/* TABS KONDISI */}
      <div className="flex flex-col gap-6 pt-4">
        
        {/* TOP: SALDO KAS REALTIME */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 shadow-xl text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
            <Landmark className="w-32 h-32" />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-sm uppercase font-bold text-slate-400 tracking-wider mb-1">Saldo Kas Pabrik Saat Ini</h3>
              <p className="text-3xl md:text-4xl font-black text-white tracking-tight">{fmt(totalKasBank)}</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl px-4 py-2 border border-slate-700/50">
              <span className="text-xs text-slate-300 font-medium">Realtime dari Bank BCA, Mandiri & BNI</span>
            </div>
          </div>
        </div>

        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* KINERJA PO STANDAR */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm relative">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="w-5 h-5 text-[#8C6208]" />
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">PO Standar</h2>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-600">Omset</span>
                <span className="text-base font-black text-[#302621]">{fmt(metrics.standar.omset)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">Total HPP</span>
                <span className="text-base font-bold text-red-600">-{fmt(metrics.standar.hpp)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">PPN Keluaran</span>
                <span className="text-base font-bold text-red-600">-{fmt(metrics.standar.ppn)}</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-sm font-bold text-[#8C6208]">Margin Kotor</span>
                <span className="text-lg font-black text-amber-600">{fmt(metrics.standar.omset - metrics.standar.hpp - metrics.standar.ppn)}</span>
              </div>
            </div>
          </div>

          {/* KINERJA INVOICE CUSTOM */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm relative">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">Invoice Custom</h2>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-600">Omset</span>
                <span className="text-base font-black text-[#302621]">{fmt(metrics.custom.omset)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">Total HPP</span>
                <span className="text-base font-bold text-red-600">-{fmt(metrics.custom.hpp)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">PPN Keluaran</span>
                <span className="text-base font-bold text-red-600">-{fmt(metrics.custom.ppn)}</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-sm font-bold text-indigo-600">Margin Kotor</span>
                <span className="text-lg font-black text-indigo-600">{fmt(metrics.custom.omset - metrics.custom.hpp - metrics.custom.ppn)}</span>
              </div>
            </div>
          </div>

          {/* KINERJA INVOICE SAMPLE */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm relative">
            <div className="flex items-center gap-2 mb-4">
              <FlaskConical className="w-5 h-5 text-pink-600" />
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">Invoice Sample</h2>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-600">Omset</span>
                <span className="text-base font-black text-[#302621]">{fmt(metrics.sample.omset)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">Total HPP</span>
                <span className="text-base font-bold text-red-600">-{fmt(metrics.sample.hpp)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">PPN Keluaran</span>
                <span className="text-base font-bold text-red-600">-{fmt(metrics.sample.ppn)}</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-sm font-bold text-pink-600">Margin Kotor</span>
                <span className="text-lg font-black text-pink-600">{fmt(metrics.sample.omset - metrics.sample.hpp - metrics.sample.ppn)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* KIRI: TOTAL PROFIT PABRIK */}
          <div className="bg-[#302621] rounded-2xl p-6 border border-[#302621] shadow-xl relative text-white">
            <div className="flex items-center gap-2 mb-6">
              <PieChart className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="text-lg font-bold text-white tracking-tight">Rangkuman Laba Rugi Pabrik</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                <span className="text-sm font-semibold text-gray-300">Total Omset Keseluruhan</span>
                <span className="text-base font-black text-white">{fmt(metrics.standar.omset + metrics.custom.omset + metrics.sample.omset)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                <span className="text-sm font-semibold text-gray-400">Total HPP Keseluruhan</span>
                <span className="text-base font-bold text-rose-400">-{fmt(metrics.standar.hpp + metrics.custom.hpp + metrics.sample.hpp)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                <span className="text-sm font-semibold text-gray-400">Total PPN Keluaran</span>
                <span className="text-base font-bold text-rose-400">-{fmt(metrics.standar.ppn + metrics.custom.ppn + metrics.sample.ppn)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                <span className="text-sm font-semibold text-[#D4AF37]">Total Margin Kotor</span>
                <span className="text-base font-bold text-[#D4AF37]">{fmt((metrics.standar.omset + metrics.custom.omset + metrics.sample.omset) - (metrics.standar.hpp + metrics.custom.hpp + metrics.sample.hpp) - (metrics.standar.ppn + metrics.custom.ppn + metrics.sample.ppn))}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                <span className="text-sm font-semibold text-gray-400">Biaya Operasional</span>
                <span className="text-base font-bold text-rose-400">-{fmt(metrics.biayaOperasional)}</span>
              </div>
              
              <div className="flex justify-between items-center py-4 bg-[#D4AF37]/10 px-4 rounded-xl border border-[#D4AF37]/30 mt-4">
                <div>
                  <span className="text-sm font-bold text-[#D4AF37] block">Margin Bersih (Profit)</span>
                  <span className="text-xs font-semibold text-[#D4AF37]/80 mt-0.5 inline-block">
                    {((metrics.standar.omset + metrics.custom.omset + metrics.sample.omset) > 0 ? (((metrics.standar.omset + metrics.custom.omset + metrics.sample.omset) - (metrics.standar.hpp + metrics.custom.hpp + metrics.sample.hpp) - (metrics.standar.ppn + metrics.custom.ppn + metrics.sample.ppn) - metrics.biayaOperasional) / (metrics.standar.omset + metrics.custom.omset + metrics.sample.omset) * 100) : 0).toFixed(1)}% dari Omset
                  </span>
                </div>
                <span className={`text-2xl font-black ${((metrics.standar.omset + metrics.custom.omset + metrics.sample.omset) - (metrics.standar.hpp + metrics.custom.hpp + metrics.sample.hpp) - (metrics.standar.ppn + metrics.custom.ppn + metrics.sample.ppn) - metrics.biayaOperasional) >= 0 ? 'text-[#D4AF37]' : 'text-rose-500'}`}>
                  {fmt(((metrics.standar.omset + metrics.custom.omset + metrics.sample.omset) - (metrics.standar.hpp + metrics.custom.hpp + metrics.sample.hpp) - (metrics.standar.ppn + metrics.custom.ppn + metrics.sample.ppn) - metrics.biayaOperasional))}
                </span>
              </div>
            </div>
          </div>

          {/* KANAN: PIUTANG, HUTANG & PROYEKSI */}
          <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4">
              <div className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                Proyeksi Masa Depan
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <h2 className="text-lg font-bold text-slate-800 tracking-tight">Proyeksi Saldo Jika Lunas Semua</h2>
            </div>

            <p className="text-[11px] text-slate-500 mb-6 bg-emerald-50/50 p-2 rounded-lg border border-emerald-50">
              Margin Bersih di sebelah kiri sebagian masih tertahan dalam wujud Piutang, dan sebagian HPP masih berwujud Hutang. Ini adalah proyeksi uang kita.
            </p>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">Saldo Kas Saat Ini</span>
                <span className="text-base font-bold text-slate-700">{fmt(totalKasBank)}</span>
              </div>
              <div className="bg-blue-50 p-3 rounded-xl border border-blue-100 flex justify-between items-center">
                <div>
                  <span className="text-xs font-semibold text-blue-800 block mb-0.5">Piutang (Uang Akan Masuk)</span>
                  <span className="text-[10px] text-blue-600">Dari klien yang belum lunas</span>
                </div>
                <span className="text-sm font-bold text-blue-700">+{fmt(metrics.standar.piutang + metrics.custom.piutang + metrics.sample.piutang)}</span>
              </div>
              <div className="bg-rose-50 p-3 rounded-xl border border-rose-100 flex justify-between items-center">
                <div>
                  <span className="text-xs font-semibold text-rose-800 block mb-0.5">Hutang (Uang Akan Keluar)</span>
                  <span className="text-[10px] text-rose-600">Ke supplier bahan yang tempo</span>
                </div>
                <span className="text-sm font-bold text-rose-700">-{fmt(metrics.hutang)}</span>
              </div>
            </div>

            <div className="flex justify-between items-center py-4 bg-emerald-50 px-4 rounded-xl border border-emerald-200 relative overflow-hidden mt-auto">
               <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 translate-x-[-100%] animate-[shimmer_3s_infinite]"></div>
               <div>
                  <span className="text-sm font-black text-emerald-800 block">Estimasi Saldo Masa Depan</span>
                  <span className="text-[10px] font-semibold text-emerald-600 mt-0.5 block">Jika semua piutang & hutang lunas</span>
               </div>
               <span className="text-2xl font-black text-emerald-700">
                  {fmt(totalKasBank + (metrics.standar.piutang + metrics.custom.piutang + metrics.sample.piutang) - metrics.hutang)}
               </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}